import { AddCircleOutline, CloseCircleOutline } from '@aslanpay/portal-common-lib';
import { Box, Button, Flex, IconButton, Input, Stack, Text } from '@chakra-ui/react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { normalizeUrl } from 'app/utils/url';

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import { LINK_LIMIT, LINK_NAME_MAX, SortableLinkRowProps } from './AdditionalLinksFields.types';

function SortableLinkRow({ field, index, onRemove }: SortableLinkRowProps) {
  const {
    register,
    control,
    watch,
    trigger,
    formState: { errors, touchedFields },
  } = useFormContext();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const linkName = watch(`links.${index}.linkName`) ?? '';

  const linkErrors = (
    errors.links as
      | Record<string, { linkUrl?: { message?: string }; linkName?: { message?: string } }>
      | undefined
  )?.[index];
  const linkTouched = (
    touchedFields.links as Record<string, { linkUrl?: boolean; linkName?: boolean }> | undefined
  )?.[index];

  return (
    <Flex
      ref={setNodeRef}
      align="flex-start"
      gap="12px"
      transform={CSS.Transform.toString(transform)}
      transition={transition}
      opacity={isDragging ? 0.4 : 1}
    >
      <Text
        cursor="grab"
        userSelect="none"
        color="brand.darkGray"
        fontSize="16px"
        mt="12px"
        {...attributes}
        style={{ touchAction: 'none' }}
        {...listeners}
      >
        ⠿
      </Text>

      <Flex flex={1} gap="12px" align="flex-start">
        <Box flex={2}>
          <Controller
            name={`links.${index}.linkUrl`}
            control={control}
            render={({ field: linkUrlField }) => {
              const displayValue = linkUrlField.value || 'https://';
              return (
                <Input
                  isInvalid={!!linkErrors?.linkUrl && !!linkTouched?.linkUrl}
                  value={displayValue}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (!val.startsWith('https://')) val = 'https://';
                    linkUrlField.onChange(val);
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasted = e.clipboardData.getData('text');
                    linkUrlField.onChange(normalizeUrl(pasted) || 'https://');
                  }}
                  onBlur={() => {
                    const current = linkUrlField.value || 'https://';
                    const normalized = current === 'https://' ? current : normalizeUrl(current);
                    if (normalized !== linkUrlField.value) linkUrlField.onChange(normalized);
                    linkUrlField.onBlur();
                    trigger(`links.${index}.linkUrl`);
                  }}
                />
              );
            }}
          />
          {linkErrors?.linkUrl?.message && linkTouched?.linkUrl && (
            <Text textStyle="extraSmallRegular" color="system.error" mt="4px">
              {linkErrors.linkUrl.message}
            </Text>
          )}
        </Box>

        <Box flex={1}>
          <Box position="relative">
            <Input
              placeholder="Button label"
              maxLength={LINK_NAME_MAX}
              isInvalid={!!linkErrors?.linkName && !!linkTouched?.linkName}
              {...register(`links.${index}.linkName`, {
                onBlur: () => trigger(`links.${index}.linkName`),
              })}
            />
            <Text
              position="absolute"
              bottom="5px"
              right="5px"
              textStyle="extraExtraSmallMedium"
              color="brand.darkGray"
            >
              {linkName.length} / {LINK_NAME_MAX}
            </Text>
          </Box>
          {linkErrors?.linkName?.message && linkTouched?.linkName && (
            <Text textStyle="extraSmallRegular" color="system.error" mt="4px">
              {linkErrors.linkName.message}
            </Text>
          )}
        </Box>
      </Flex>

      <IconButton
        variant="link"
        aria-label="Remove link"
        icon={<CloseCircleOutline boxSize="24px" color="brand.purple" />}
        mt="12px"
        onClick={() => onRemove(index)}
      />
    </Flex>
  );
}

function AdditionalLinksFields() {
  const { control } = useFormContext();

  const { fields, append, remove, move } = useFieldArray({ control, name: 'links' });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((field) => field.id === active.id);
    const newIndex = fields.findIndex((field) => field.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    move(oldIndex, newIndex);
  };

  return (
    <Stack spacing="5px" mt="5px">
      {fields.length > 0 && (
        <Text textAlign="right" textStyle="smallMedium" color="brand.mutedPurple">
          {fields.length}/{LINK_LIMIT}
        </Text>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={fields.map((field) => field.id)}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing="10px">
            {fields.map((field, index) => (
              <SortableLinkRow key={field.id} field={field} index={index} onRemove={remove} />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      <Button
        alignSelf="flex-start"
        variant="link"
        leftIcon={<AddCircleOutline boxSize="20px" />}
        onClick={() => append({ linkUrl: 'https://', linkName: '' })}
        isDisabled={fields.length >= LINK_LIMIT}
        mt="15px"
        p="0"
      >
        Add link
      </Button>
    </Stack>
  );
}

export default AdditionalLinksFields;
